import React, { ReactElement, useContext, useEffect } from "react";
import { sanitize } from "dompurify";
import { AppContext, BookingData } from "../../AppDataProvider/AppDataProvider";
import { StepTitle as BaseStepTitle, Button } from "../../common/components";
import { corners, gap, margin, marginBottom, media, typography } from "../../common/styles";
import { useApolloClient } from "@apollo/client";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ActionType } from "../../AppDataProvider/actions";
import { BookingVisibilityType } from "../../GeneratedGraphQL";
import { Mixpanel } from "../../mixpanel";
import { requireBookingDataFields } from "../../utils";
import { PartRequired } from "../../utils/types";
import Summary from "./Summary/Summary";
import CustomerTranslations from "../../utils/CustomerTranslations";
type BookingCreatedBookingData = PartRequired<BookingData, "organisation" | "service" | "userDetails">;
export function BookingCreatedStep(): ReactElement {
  const {
    t
  } = useTranslation();
  const {
    translateEntity
  } = CustomerTranslations.useCustomerTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const client = useApolloClient();
  useEffect(() => {
    Mixpanel.track({
      name: "Booking created"
    });
  }, []);
  requireBookingDataFields<BookingCreatedBookingData>(appData.bookingData, ["organisation", "service", "userDetails"]);
  const {
    organisation,
    service,
    totalPaid,
    userDetails
  } = appData.bookingData;
  const {
    areConfirmationEmailsEnabled,
    areInvoiceEmailsEnabled,
    bookingConfirmationMessage
  } = organisation;
  const isRequestBooking = service.bookingVisibilityType === BookingVisibilityType.Request;
  const showEmailLine = areConfirmationEmailsEnabled || areInvoiceEmailsEnabled && totalPaid !== undefined;
  const makeAnotherBooking = () => {
    void client.clearStore().then(() => {
      appDataDispatch({
        type: ActionType.RESTART_BOOKING_FLOW
      });
    });
  };
  const translatedService = translateEntity({
    document: service,
    type: "service"
  });
  return <Container data-sentry-element="Container" data-sentry-component="BookingCreatedStep" data-sentry-source-file="BookingCreatedStep.tsx">
      <Title data-sentry-element="Title" data-sentry-source-file="BookingCreatedStep.tsx">
        <CheckIcon data-sentry-element="CheckIcon" data-sentry-source-file="BookingCreatedStep.tsx" />
        <StepTitle data-sentry-element="StepTitle" data-sentry-source-file="BookingCreatedStep.tsx">
          {t(isRequestBooking ? "stepTitle.bookingRequested" : "stepTitle.bookingCreated")}
        </StepTitle>
      </Title>
      {service.image && <ServiceImage src={service.image} alt={translatedService.name} />}
      {bookingConfirmationMessage && <Text dangerouslySetInnerHTML={{
      __html: sanitize(translateEntity({
        document: organisation,
        type: "account"
      }).bookingConfirmationMessage, {
        ADD_ATTR: ["target"]
      })
    }} />}
      <Summary data-sentry-element="Summary" data-sentry-source-file="BookingCreatedStep.tsx" />
      {showEmailLine && <Text className="sentry-mask">
          {t("confirmationEmailSent", {
        email: userDetails.email
      })}
        </Text>}
      <Button variant="secondary" onClick={makeAnotherBooking} data-sentry-element="Button" data-sentry-source-file="BookingCreatedStep.tsx">
        {t("makeAnotherBooking")}
      </Button>
    </Container>;
}
export default BookingCreatedStep;
const Container = styled.div`
  width: 100%;
  max-width: 22.5rem;
  align-self: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const Title = styled.div`
  display: flex;

  flex-direction: row;

  ${gap.m}

  @media ${media.mobile} {
    flex-direction: column;
  }

  align-items: center;
  justify-content: center;
  ${marginBottom.m}
`;
const CheckIcon = styled(FontAwesomeIcon).attrs({
  icon: faCheckCircle,
  size: "2x"
})`
  color: ${({
  theme
}) => theme.colours.success};
`;
const StepTitle = styled(BaseStepTitle)`
  ${margin.zero}
`;
const ServiceImage = styled.img`
  width: 100%;
  object-fit: cover;
  ${corners.m}
  ${marginBottom.m}
`;
const Text = styled.div`
  ${typography.body}
  ${marginBottom.m}
  /*
   * Was unset — this text (the sanitised bookingConfirmationMessage, and
   * the "confirmation email sent to X" line) sits directly in this step's
   * plain Container, which never sets an ambient text colour, so it fell
   * back to the browser default black and was invisible on dark-theme
   * brands. Summary.tsx's own card content was already fixed to use
   * --d-text explicitly; this sibling text wasn't.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;